import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Grid,
  Autocomplete,
  Typography,
  FormHelperText,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip } from
'@mui/material';
import { AutoAwesome as AutoGenerateIcon, LocationOn } from '@mui/icons-material';
import { LOCATION_TYPES } from '../../config/constants';
import locationService from '../../services/locationService';
import TreeSelect from '../TreeSelect';
import ResponsiveDialog from '../ResponsiveDialog';import { useThemeSettings } from "../../contexts/ThemeContext";

const LocationDialog = ({
  open,
  mode = 'create',
  location = null,
  parentLocation = null,
  onClose,
  onSave
}) => {const { t } = useThemeSettings();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    locationType: 5, // Default to Room
    parent: null,
    isActive: true,
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      postalCode: ''
    },
    metadata: {}
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [treeSelectOpen, setTreeSelectOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);

  useEffect(() => {
    if (open) {
      loadLocations();
      if (mode === 'edit' && location) {
        setFormData({
          name: location.friendlyName || location.name || '',
          description: location.description || '',
          locationType: location.locationType || location.type || 5,
          parent: location.parentLocation || location.parent || location.parentId || null,
          isActive: location.isActive !== undefined ? location.isActive : true,
          address: location.address || {
            street: '',
            city: '',
            state: '',
            country: '',
            postalCode: ''
          },
          metadata: location.metadata || {}
        });
      } else if (mode === 'create') {
        setFormData({
          name: '',
          description: '',
          locationType: 5, // Default to Room
          parent: parentLocation?.id || parentLocation?._id || null,
          isActive: true,
          address: {
            street: '',
            city: '',
            state: '',
            country: '',
            postalCode: ''
          },
          metadata: {}
        });
      }
      setErrors({});
    }
  }, [open, mode, location, parentLocation]);

  const loadLocations = async () => {
    setLoadingLocations(true);
    try {
      const locations = await locationService.getLocations();
      //console.log('LocationDialog - Loaded locations:', locations?.length || 0, 'locations');
      setAllLocations(Array.isArray(locations) ? locations : []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setAllLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Location name is required';
    }

    if (formData.name.length > 100) {
      newErrors.name = 'Location name cannot exceed 100 characters';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    if (!formData.locationType) {
      newErrors.locationType = 'Location type is required';
    }

    // Non-site locations must have a parent
    if (formData.locationType !== 1 && !formData.parent) {
      newErrors.parent = 'Parent location is required for non-site locations';
    }

    // Prevent circular reference
    if (mode === 'edit' && formData.parent === (location?.id || location?._id)) {
      newErrors.parent = 'Location cannot be its own parent';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const dataToSubmit = {
        friendlyName: formData.name.trim(),
        description: formData.description.trim(),
        locationType: formData.locationType,
        parentLocation: formData.parent,
        isActive: formData.isActive,
        address: formData.address,
        metadata: formData.metadata
      };

      if (mode === 'edit') {
        await locationService.updateLocation(location.id || location._id, dataToSubmit);
      } else {
        await locationService.createLocation(dataToSubmit);
      }

      onSave && onSave();
      handleClose();
    } catch (error) {
      console.error('Error saving location:', error);
      setErrors({
        submit: error.message || 'Failed to save location. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAutoGenerateName = () => {
    const selectedType = LOCATION_TYPES.find((t) => t.id === formData.locationType);
    const parentLoc = allLocations.find((loc) =>
    (loc.id || loc._id) === formData.parent
    );

    if (!selectedType) {
      setErrors({ name: 'Please select a location type first' });
      return;
    }

    let generatedName = '';

    // Helper function to extract site code from any location name
    const extractSiteCode = (locationName) => {
      // Check if name already starts with 2 letters + 2 numbers pattern
      const siteCodeMatch = locationName.match(/^([A-Z]{2})(\d{2})/);
      if (siteCodeMatch) {
        return siteCodeMatch[1] + siteCodeMatch[2]; // e.g., "SH20"
      }
      return null;
    };

    // Helper function to find the site code from the hierarchy
    const findSiteCode = (location) => {
      if (!location) return null;

      // Check if this location has a site code
      const code = extractSiteCode(location.friendlyName || location.name || '');
      if (code) return code;

      // If not, check parent hierarchy
      if (location.parentLocation || location.parent) {
        const parent = allLocations.find((loc) =>
        (loc.id || loc._id) === (location.parentLocation || location.parent)
        );
        return findSiteCode(parent);
      }

      return null;
    };

    if (formData.locationType === 1) {
      // Site - Generate 2-letter + 2-number code
      const existingSites = allLocations.filter((l) => l.locationType === 1);

      // Find the next available site code
      let siteNumber = 1;
      let siteLetters = 'ST'; // Default site prefix

      // You can customize the letters based on your naming convention
      // For example, prompt user or use first 2 letters of company name
      const promptLetters = window.prompt('Enter 2-letter site code (e.g., SH for Shanghai):', 'ST');
      if (promptLetters && promptLetters.match(/^[A-Z]{2}$/i)) {
        siteLetters = promptLetters.toUpperCase();
      }

      // Find next available number for this letter combination
      existingSites.forEach((site) => {
        const siteName = site.friendlyName || site.name || '';
        const match = siteName.match(new RegExp(`^${siteLetters}(\\d{2})`));
        if (match) {
          const num = parseInt(match[1]);
          if (num >= siteNumber) {
            siteNumber = num + 1;
          }
        }
      });

      generatedName = `${siteLetters}${String(siteNumber).padStart(2, '0')}`;
    } else if (parentLoc) {
      // Has parent - find site code from hierarchy and use it as prefix
      const siteCode = findSiteCode(parentLoc);

      if (siteCode) {
        // Use site code as base
        const typeAbbr = selectedType.value.substring(0, 2).toUpperCase();

        // Count similar locations under same parent
        const similarLocs = allLocations.filter((l) =>
        (l.parentLocation === formData.parent || l.parent === formData.parent) &&
        l.locationType === formData.locationType
        );
        const locNumber = similarLocs.length + 1;

        // Generate name: SiteCode-TypeAbbr-Number
        // For buildings: SH20-B01, SH20-B02
        // For floors: SH20-F01, SH20-F02
        // For rooms: SH20-R01, SH20-R02
        const typePrefix = {
          2: 'B', // Building
          3: 'F', // Floor
          4: 'U', // Unit
          5: 'R', // Room
          6: 'H', // Hallway
          7: 'WC', // Restroom (Water Closet)
          8: 'S', // Stock
          9: 'P', // Parking
          10: 'CA', // Common Area
          11: 'BM', // Basement
          12: 'G', // Garden
          13: 'FT', // Fitness
          14: 'HD', // Hairdresser
          15: 'W', // Wellness
          16: 'PM', // Physical Medicine
          17: 'RS', // Restaurant
          18: 'BS' // Bicycle Storage
        }[formData.locationType] || typeAbbr.charAt(0);

        generatedName = `${siteCode}-${typePrefix}${String(locNumber).padStart(2, '0')}`;
      } else {
        // Fallback if no site code found in hierarchy
        const parentName = parentLoc.friendlyName || parentLoc.name || 'LOC';
        const parentCode = parentName.substring(0, 4).toUpperCase();
        const typeAbbr = selectedType.value.substring(0, 2).toUpperCase();
        generatedName = `${parentCode}-${typeAbbr}01`;
      }
    } else {
      // No parent but not a site - shouldn't happen due to validation
      generatedName = `${selectedType.value.toUpperCase()}-01`;
    }

    setFormData((prev) => ({
      ...prev,
      name: generatedName
    }));

    // Clear name error if any
    if (errors.name) {
      setErrors((prev) => ({
        ...prev,
        name: undefined
      }));
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      locationType: 5, // Default to Room
      parent: null,
      isActive: true,
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
      },
      metadata: {}
    });
    setErrors({});
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  // Filter out current location and its descendants from parent options
  const getAvailableParents = () => {
    if (mode === 'create') return allLocations;

    const currentId = location?.id || location?._id;
    return allLocations.filter((loc) => {
      const locId = loc.id || loc._id;
      // Don't allow selecting itself or its descendants
      return locId !== currentId && !loc.path?.includes(currentId);
    });
  };

  // Update selected parent when form parent changes
  useEffect(() => {
    if (formData.parent && allLocations.length > 0) {
      const parent = allLocations.find((loc) =>
      (loc.id || loc._id || loc.location_UUID) === formData.parent
      );
      setSelectedParent(parent || null);
    } else {
      setSelectedParent(null);
    }
  }, [formData.parent, allLocations]);

  const handleParentSelect = (parent) => {
    setSelectedParent(parent);
    handleChange('parent', parent ? parent.id || parent._id || parent.location_UUID : null);
    setTreeSelectOpen(false);
  };

  return (
    <>
      <ResponsiveDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'edit' ? 'Edit Location' : 'Create New Location'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {errors.submit &&
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
            }
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                  fullWidth
                  label={t ? t("LocationDialog.location_name") : "Location Name"}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name || 'Enter a name or click auto-generate'}
                  required
                  disabled={loading}
                  inputProps={{ maxLength: 100 }}
                  InputProps={{
                    endAdornment:
                    <InputAdornment position="end">
                      <Tooltip title={t ? t("LocationDialog.autogenerate_name_based_type_pare") : "Auto-generate name based on type and parent"}>
                        <IconButton
                          onClick={handleAutoGenerateName}
                          edge="end"
                          disabled={loading || !formData.locationType}>
                          
                          <AutoGenerateIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>

                  }} />
                
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.locationType}>
                <InputLabel>{t ? t("LocationDialog.location_type") : "Location Type *"}</InputLabel>
                <Select
                    value={formData.locationType}
                    onChange={(e) => handleChange('locationType', e.target.value)}
                    label={t ? t("LocationDialog.location_type") : "Location Type *"}
                    disabled={loading}
                    required>
                    
                  {LOCATION_TYPES.map((type) =>
                    <MenuItem key={type.id} value={type.id}>
                      {type.friendlyName}
                    </MenuItem>
                    )}
                </Select>
                {formData.locationType === 1 &&
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{t ? t("LocationDialog.site_locations_dont_require_parent") : "Site locations don't require a parent"}

                  </Typography>
                  }
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                  fullWidth
                  label={t ? t("LocationDialog.description") : "Description"}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  error={!!errors.description}
                  helperText={errors.description}
                  multiline
                  rows={3}
                  disabled={loading}
                  inputProps={{ maxLength: 500 }} />
                
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                  fullWidth
                  label={t ? t("LocationDialog.parent_location") : "Parent Location"}
                  value={selectedParent ? `${selectedParent.friendlyName || selectedParent.name || 'Unnamed'}` : ''}
                  onClick={() => !loading && !loadingLocations && formData.locationType !== 1 && setTreeSelectOpen(true)}
                  error={!!errors.parent}
                  helperText={errors.parent || (formData.locationType === 1 ? 'Optional for Site locations' : 'Required for non-Site locations')}
                  required={formData.locationType !== 1}
                  disabled={loading || loadingLocations || formData.locationType === 1}
                  InputProps={{
                    readOnly: true,
                    endAdornment: selectedParent &&
                    <InputAdornment position="end">
                      <Chip
                        size="small"
                        icon={<LocationOn />}
                        label={LOCATION_TYPES.find((t) => t.id === selectedParent.locationType)?.friendlyName || 'Location'}
                        onDelete={() => {
                          setSelectedParent(null);
                          handleChange('parent', null);
                        }}
                        disabled={loading || formData.locationType === 1} />
                      
                    </InputAdornment>,

                    style: { cursor: formData.locationType !== 1 ? 'pointer' : 'default' }
                  }}
                  placeholder={formData.locationType === 1 ? 'Not required for Site' : 'Click to select parent location'} />
                
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                  control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    disabled={loading} />

                  }
                  label={t ? t("LocationDialog.active") : "Active"} />

            </Grid>

            {(formData.locationType === 1 || formData.locationType === 2) &&
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>{t ? t("LocationDialog.address_information") : "Address Information"}

                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t ? t("LocationDialog.street_address") : "Street Address"}
                    value={formData.address.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    disabled={loading} />
                  
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t ? t("LocationDialog.city") : "City"}
                    value={formData.address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    disabled={loading} />
                  
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t ? t("LocationDialog.stateprovince") : "State/Province"}
                    value={formData.address.state}
                    onChange={(e) => handleAddressChange('state', e.target.value)}
                    disabled={loading} />
                  
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t ? t("LocationDialog.country") : "Country"}
                    value={formData.address.country}
                    onChange={(e) => handleAddressChange('country', e.target.value)}
                    disabled={loading} />
                  
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t ? t("LocationDialog.postal_code") : "Postal Code"}
                    value={formData.address.postalCode}
                    onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                    disabled={loading} />
                  
                </Grid>
              </>
              }
          </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>{t ? t("LocationDialog.cancel") : "Cancel"}

          </Button>
        <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}>
            
          {mode === 'edit' ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
      </ResponsiveDialog>

      <TreeSelect
        open={treeSelectOpen}
        onClose={() => setTreeSelectOpen(false)}
        onSelect={handleParentSelect}
        data={getAvailableParents()}
        selectedId={formData.parent}
        title={t ? t("LocationDialog.select_parent_location") : "Select Parent Location"}
        type="location"
        loading={loadingLocations}
        allowNone={formData.locationType === 1} />
      
    </>);

};

export default LocationDialog;